import React, { useMemo } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { EquipmentGraphWidget } from '../components/Graphs'
import { CheckValue, DetailTable } from '../components/DetailTable'
import { MetricWidget, WidgetIcon } from '../components/MetricWidget'
import { InfoRow, Widget } from '../components/Widget'
import { AP_CONNECTED_STATIONS_GRAPH, AP_REACHABILITY_GRAPH } from '../../../../operations-gql/queries'
import { buildHistoryGraphData, buildLineData, formatDecimal, getApUptimeDays, getFirstMetricValue } from '../utils'

const ChannelsWidget = ({ equipment }) => {
  const { t } = useTranslation()

  return (
    <Box
      sx={{
        minHeight: 72,
        border: '1px solid #EFEFEF',
        borderRadius: '32px',
        backgroundColor: '#fff',
        px: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.25,
        minWidth: 0
      }}
    >
      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <WidgetIcon icon="fa-solid fa-waveform" />
        <Typography sx={{ color: '#464646', fontSize: 12, fontWeight: 500 }}>{t('supervision.hardware.ap.channels')}</Typography>
      </Stack>
      <Stack gap={0} sx={{ width: '50%', minWidth: 118, ml: 'auto' }}>
        <InfoRow
          label={t('supervision.hardware.ap.channel_24')}
          value={equipment.channel_g}
          sx={{ minHeight: 22, justifyContent: 'space-between' }}
        />
        <InfoRow
          label={t('supervision.hardware.ap.channel_5')}
          value={equipment.channel_a}
          sx={{ minHeight: 22, justifyContent: 'space-between' }}
        />
      </Stack>
    </Box>
  )
}

const ApMetricsWidgets = ({ equipment }) => {
  const { t } = useTranslation()
  const cpu = getFirstMetricValue(equipment, ['cpu_usage', 'ap_cpu_usage', 'AP CPU usage'])
  const memory = getFirstMetricValue(equipment, ['memory_usage', 'ap_memory_usage', 'AP Memory usage'])
  const uptime = getApUptimeDays(equipment)
  const snmp = equipment.is_snmp_reachable

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ChannelsWidget equipment={equipment} />
      </Box>
      <Box sx={{ flex: 2, minWidth: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
        <MetricWidget title={t('supervision.hardware.ap.cpu')} value={cpu == null ? null : formatDecimal(cpu)} unit="%" icon="fa-solid fa-microchip" progress={cpu == null ? null : Number(cpu)} showProgress />
        <MetricWidget title={t('supervision.hardware.ap.ram')} value={memory == null ? null : formatDecimal(memory)} unit="%" icon="fa-solid fa-memory" progress={memory == null ? null : Number(memory)} showProgress />
        <MetricWidget title={t('supervision.hardware.ap.uptime')} value={uptime == null ? null : formatDecimal(uptime, 2)} unit={t('supervision.hardware.ap.days_unit')} icon="fa-solid fa-clock" />
        <MetricWidget
          title={t('supervision.hardware.ap.snmp')}
          value={snmp == null ? null : t(snmp ? 'supervision.hardware.ap.active' : 'supervision.hardware.ap.inactive')}
          icon="fa-solid fa-network-wired"
          iconColor={snmp ? '#92BC62' : '#3D3D3D'}
        />
      </Box>
    </Stack>
  )
}

const SsidsWidget = ({ equipment }) => {
  const { t } = useTranslation()
  const ssids = equipment.ssids || equipment.ssid_names || []

  const rows = ssids.map(ssid => typeof ssid === 'string'
    ? { id: ssid, name: ssid, has24: null, has5: null }
    : { id: ssid.name, name: ssid.name, has24: ssid.has_2_4ghz, has5: ssid.has_5ghz })

  return (
    <Widget sx={{ flex: 1, minHeight: 220 }}>
      <DetailTable
        columns={[
          { key: 'name', label: t('supervision.card.ssid_name'), width: '70%' },
          { key: 'has24', label: t('supervision.card.24_ghz'), width: '15%', align: 'center', render: row => <CheckValue value={row.has24} /> },
          { key: 'has5', label: t('supervision.card.5_ghz'), width: '15%', align: 'center', render: row => <CheckValue value={row.has5} /> },
        ]}
        rows={rows}
      />
    </Widget>
  )
}

const ApGraphsRow = ({ equipment }) => {
  const { t } = useTranslation()
  const { data: connectedData, loading: connectedLoading } = useQuery(AP_CONNECTED_STATIONS_GRAPH, {
    variables: { sf_id: equipment?.site?.sf_id, macs: [equipment?.mac], timeRange: { preset: '-1d' } },
    skip: !equipment?.site?.sf_id || !equipment?.mac
  })
  const { data: reachabilityData, loading: reachabilityLoading } = useQuery(AP_REACHABILITY_GRAPH, {
    variables: { mac: equipment?.mac, timeRange: { preset: '-1d' } },
    skip: !equipment?.mac
  })

  const connectedGraph = useMemo(() => {
    const graph = connectedData?.aps?.[0]
    return buildLineData({ timestamps: graph?.stamp || [], values: graph?.connected_stations || [] })
  }, [connectedData])
  const reachabilityGraph = useMemo(() => {
    return buildHistoryGraphData(reachabilityData?.apHistory)
  }, [reachabilityData])

  return (
    <Stack direction={{ xs: 'column', lg: 'row' }} gap={2}>
      <SsidsWidget equipment={equipment} />
      <EquipmentGraphWidget
        title={t('supervision.hardware.ap.connected_devices_evolution')}
        data={connectedGraph}
        color="#A5D9CB"
        loading={connectedLoading}
        modalQuery={AP_CONNECTED_STATIONS_GRAPH}
        modalVariables={{ sf_id: equipment?.site?.sf_id, macs: [equipment?.mac] }}
        mapModalData={data => {
          const graph = data?.aps?.[0]
          return { data: buildLineData({ timestamps: graph?.stamp || [], values: graph?.connected_stations || [] }) }
        }}
      />
      <EquipmentGraphWidget
        title={t('supervision.hardware.ap.reachability')}
        data={reachabilityGraph}
        color="#7FAFC4"
        loading={reachabilityLoading}
        modalQuery={AP_REACHABILITY_GRAPH}
        modalVariables={{ mac: equipment?.mac }}
        mapModalData={data => ({ data: buildHistoryGraphData(data?.apHistory) })}
      />
    </Stack>
  )
}

const AccessPointSections = ({ equipment }) => (
  <>
    <ApMetricsWidgets equipment={equipment} />
    <ApGraphsRow equipment={equipment} />
  </>
)

export default AccessPointSections
